import { createRouter, createWebHistory, RouteRecordRaw } from 'vue-router';

import AuthLayout from '@layouts/AuthLayout.vue';
import AppLayout from '@layouts/AppLayout.vue';
import NoUILayout from '@layouts/NoUILayout.vue';
import Base from '@pages/Base.vue';
import { useUserStore } from '@stores/user-store';
import { matchPaths } from '@services/utils';
import { getAuthentication } from '@services/auth';

const routes: Array<RouteRecordRaw> = [
  {
    name: 'homepage',
    path: '/',
    children: [
      {
        path: '',
        component: Base,
      },
      {
        name: 'town-council',
        path: 'towncouncil',
        component: AppLayout,
        meta: {
          requiresAuth: true,
          parentRoutePath: 'towncouncil',
        },
        children: [
          {
            name: 'townCouncilContractorsList',
            path: 'contractors',
            component: () => import('@pages/town-council/ContractorsPage.vue'),
          },
          {
            name: 'ContractorDetails',
            path: 'contractors/:ContractorId',
            component: () => import('@pages/town-council/ContractorDetailsPage.vue'),
            props: true, // Allows passing params as props
          },
          {
            name: 'townCouncilContractSchedule',
            path: 'contract-schedule',
            component: () => import('@pages/town-council/ContractSchedulePage.vue'),
          },
          {
            name: 'newContractSchedule',
            path: 'contract-schedule/new',
            component: () => import('@pages/town-council/NewContractSchedulePage.vue'),
          },
          {
            name: 'townCouncilDoors',
            path: 'locations',
            component: () => import('@pages/town-council/DoorsPage.vue'),
          },
          {
            name: 'townCouncilWorkerApproval',
            path: 'worker-approval',
            component: () => import('@pages/town-council/ContractWorkerRequestsPage.vue'),
          },
          {
            name: 'townCouncilAdhocApproval',
            path: 'adhoc-approval',
            component: () => import('@pages/town-council/AdhocApprovalPage.vue'),
          },
          {
            name: 'townCouncilAdHocAccess',
            path: 'adhoc-access',
            component: () => import('@pages/town-council/AdhocSchedulePage.vue'),
          },
          {
            name: 'newAdhocAccess',
            path: 'adhoc-access/new',
            component: () => import('@pages/town-council/NewAdhocAccessPage.vue'),
          },
          {
            name: 'townCouncilAudit',
            path: 'audit',
            component: () => import('@pages/town-council/AuditPage.vue'),
          },
          {
            name: 'townCouncilAccess',
            path: 'access',
            component: () => import('@pages/town-council/AccessPage.vue'),
          },
          {
            name: 'townCouncilAccount',
            path: 'account',
            component: () => import('@pages/town-council/AccountPage.vue'),
          },
          {
            name: 'townCouncilOverview',
            path: 'overview',
            component: () => import('@pages/town-council/OverviewPage.vue'),
          },
          {
            name: 'townCouncilDefault',
            path: '',
            redirect: {
              path: '/towncouncil/overview',
            },
          },
        ],
      },
      {
        name: 'contractors',
        path: 'contractor',
        redirect: { name: 'contractSchedule' },
        component: AppLayout,
        meta: {
          requiresAuth: true,
          showReminder: true,
          parentRoutePath: 'contractor',
        },
        children: [
          {
            name: 'contractorReports',
            path: 'reports',
            component: Base,
          },
          {
            name: 'contractSchedule',
            path: 'schedule',
            component: () => import('@pages/contractor/SchedulesPage.vue'),
          },
          {
            name: 'contractorWorker',
            path: 'workers',
            component: () => import('../pages/contractor/WorkersPage.vue'),
          },
          {
            name: 'contractorWorkerApproval',
            path: 'approvals',
            component: () => import('../pages/contractor/WorkerApprovalsPage.vue'),
          },
          {
            name: 'contractorAccount',
            path: 'account',
            component: () => import('../pages/contractor/AccountPage.vue'),
          },
        ],
      },
      {
        name: 'adhoc',
        path: 'adhoc',
        redirect: { name: 'adhocRegister' },
        component: NoUILayout,
        children: [
          {
            name: 'adhocRegister',
            path: 'adhoc-register',
            component: () => import('@pages/adhoc/AdhocRegisterPage.vue'),
          },
          {
            name: 'privacyPolicy',
            path: 'privacy-policy',
            component: () => import('@pages/adhoc/PrivacyPolicyPage.vue'),
          },
        ],
      },
      {
        path: 'auth',
        component: AuthLayout,
        children: [
          {
            name: 'login',
            path: 'login',
            component: () => import('@pages/auth/Login.vue'),
          },
          {
            name: 'signup',
            path: 'signup',
            component: () => import('@pages/auth/Signup.vue'),
          },
          {
            name: 'recover-password',
            path: 'recover-password',
            component: () => import('@pages/auth/RecoverPassword.vue'),
          },
          {
            name: 'recover-password-email',
            path: 'recover-password-email',
            component: () => import('@pages/auth/CheckTheEmail.vue'),
          },
          {
            name: 'logout',
            path: 'logout',
            component: () => import('@pages/auth/Logout.vue'),
          },
          {
            path: '',
            redirect: { name: 'login' },
          },
        ],
      },
    ],
  },
  {
    name: '404',
    path: '/404',
    component: () => import('@pages/404.vue'),
  },
];

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  scrollBehavior(to, from, savedPosition) {
    if (savedPosition) {
      return savedPosition;
    }
    // For some reason using documentation example doesn't scroll on page navigation.
    if (to.hash) {
      return { el: to.hash, behavior: 'smooth' };
    } else {
      window.scrollTo(0, 0);
    }
  },
  routes,
});

router.beforeEach(async (to) => {
  console.log('hitting router beforeEach');
  const user = useUserStore();

  if (to.meta.requiresAuth) {
    const isLoggedInOnAWS = await getAuthentication();
    if (isLoggedInOnAWS) {
      await user.getCurrentAuthSession();
    }

    if (user.isLoggedIn && user.pagePermissions.length === 0) {
      await user.fetchPagePermissions();
    }

    if (user.isLoggedIn && user.hasProfile === false) {
      try {
        await user.fetchProfileId();
        await user.fetchUserProfile();
      } catch {
        await user.logout();
      }
    }
    console.log('route requires auth, checking');

    if (user.isLoggedIn === false) {
      console.log('user not logged in');
      return {
        name: 'login',
        query: { redirect: to.fullPath },
      };
    } else if (matchPaths(to.fullPath, user.pagePermissions) === false) {
      console.group('user is logged in, but do not have permissions for this page');
      console.log(to.fullPath);
      console.log(user.pagePermissions);
      console.groupEnd();
      return {
        name: 'logout',
      };
    }
  } else if (to.path.match('/auth/login')) {
    console.log('path is /auth/login');
    if (user.isLoggedIn) {
      console.log('user is logged in');
      return {
        path: typeof to.query.redirect === 'string' ? to.query.redirect : '/',
      };
    }
  } else {
    console.log('path does not require auth');
  }
});

export default router;
